import { useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useBible } from '../context/BibleContext';
import {
  useBiblesQuery,
  useBooksQuery,
  useSearchQuery,
  usePrefetchPanels,
  useContentVersion,
  prefetchPanelData,
} from '../hooks/queries';
import BiblePanel from './BiblePanel';
import SearchResults from './SearchResults';

interface BookViewProps {
  searchQuery: string;
  onClearSearch: () => void;
}

export default function BookView({ searchQuery, onClearSearch }: BookViewProps) {
  const queryClient = useQueryClient();
  const contentVersion = useContentVersion();
  const { openBibles, addOpenBible, removeOpenBible, validateOpenBibles } = useBible();
  const { data: bibles = [], isLoading: biblesLoading, error: biblesError } = useBiblesQuery();
  const { data: books = [], isLoading: booksLoading } = useBooksQuery();
  const { data: searchResults } = useSearchQuery(searchQuery);

  // Fetch verses/chapters for panels restored from localStorage in parallel with bibles/books,
  // instead of waiting for the loading gate below to mount BiblePanel (which caused a 2nd request wave).
  usePrefetchPanels(openBibles);

  // Validate panels whenever bibles/books data changes (handles delete/import)
  useEffect(() => {
    if (bibles.length > 0 && books.length > 0) {
      validateOpenBibles(bibles, books);
    }
  }, [bibles, books, validateOpenBibles]);

  // Create default panel if none exist. This is the first-visit path, where nothing was restored from
  // localStorage and the ids only become known now. Requesting the panel's content here, in the same
  // tick, rather than leaving it to BiblePanel means the requests do not wait out addOpenBible's state
  // commit, its localStorage write and a full render pass before they reach the network.
  useEffect(() => {
    if (openBibles.length === 0 && bibles.length > 0 && books.length > 0) {
      prefetchPanelData(queryClient, bibles[0].id, books[0].id, contentVersion);
      addOpenBible(bibles[0].id, books[0].id);
    }
  }, [openBibles.length, bibles, books, addOpenBible, queryClient, contentVersion]);

  const handleAddPanel = useCallback(() => {
    if (bibles.length > 0 && books.length > 0) {
      addOpenBible(bibles[0].id, books[0].id);
    }
  }, [bibles, books, addOpenBible]);

  if (biblesError) {
    return (
      <div className="h-full flex items-center justify-center p-6">
        <div className="rounded-2xl p-8 text-center max-w-md bg-white shadow-lg text-red-600 dark:bg-ink-800 dark:text-red-400">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <p className="font-bold text-lg">Error loading content</p>
          <p className="text-sm mt-2">{(biblesError as Error).message}</p>
        </div>
      </div>
    );
  }

  if (searchResults) {
    return (
      <SearchResults
        results={searchResults}
        query={searchQuery}
        bibles={bibles}
        canonicalBooks={books}
        onClear={onClearSearch}
      />
    );
  }

  if (biblesLoading || booksLoading || openBibles.length === 0) {
    return (
      <div className="h-full flex items-center justify-center p-6">
        <div className="rounded-2xl p-12 text-center max-w-md bg-white shadow-lg text-gray-500 dark:bg-ink-800 dark:text-gray-400">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto mb-4 text-gray-300 dark:text-ink-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
          </svg>
          <p className="text-lg font-bold text-gray-700 dark:text-parchment-100">Loading...</p>
        </div>
      </div>
    );
  }

  let gridCols = 'grid-cols-1';
  if (openBibles.length === 2) {
    gridCols = 'grid-cols-1 md:grid-cols-2';
  } else if (openBibles.length >= 3) {
    gridCols = 'grid-cols-1 md:grid-cols-3';
  }

  // A lone panel stretched to the full window gives lines far too long to read comfortably, so cap
  // it just above the width it would have as one of two, and centre it. Narrow screens show one
  // full-width column anyway, so this only applies from md up.
  const singlePanelWidth = openBibles.length === 1 ? 'md:max-w-[55%] md:mx-auto' : '';

  return (
    <div className={`relative h-full ${singlePanelWidth}`}>
      <div className={`grid ${gridCols} gap-4 h-full min-h-0 overflow-y-auto md:overflow-hidden`}>
        {openBibles.map((openBible) => (
          <BiblePanel
            key={openBible.id}
            openBibleId={openBible.id}
            bibles={bibles}
            canonicalBooks={books}
            onRemove={() => removeOpenBible(openBible.id)}
            isOnlyPanel={openBibles.length === 1}
          />
        ))}
      </div>

      {/* Add panel — floats over the last column, and disappears once three are open */}
      {openBibles.length < 3 && (
        <button
          onClick={handleAddPanel}
          title="Add new Bible translation"
          aria-label="Add new Bible translation"
          className="absolute bottom-6 right-6 h-12 w-12 rounded-full shadow-lg flex items-center justify-center transition-colors bg-parchment-300 text-ink-900 hover:bg-parchment-200 dark:bg-parchment-300 dark:hover:bg-parchment-200"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
          </svg>
        </button>
      )}
    </div>
  );
}
